import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY,
  NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY,
  NEWBUILDING_PURCHASE_GIFT_REGIONS,
  NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY,
} from 'shared/constants/newbuildingPurchaseGiftPromo';
import { isInRegionSelector } from 'shared/selectors/offerData';

import { NewbuildingPurchaseGiftExperiment } from './NewbuildingPurchaseGiftExperiment';
import { NewbuildingPurchaseGiftModalLoadable } from './NewbuildingPurchaseGiftModalLoadable';
import {
  INewbuildingPurchaseGiftPromoContext,
  NewbuildingPurchaseGiftPromoContext,
  TNewbuildingPurchaseGiftPromoSource,
} from './NewbuildingPurchaseGiftPromoContext';
import {
  trackCopyEmail,
  trackEntryPointClick,
  trackEntryPointShow,
  trackModalClose,
  trackModalConfirm,
  trackModalShow,
  trackStepView,
  trackTermsClick,
} from './tracking';
import { useApplicationContext } from '../ApplicationContext';

const selectIsInPromoRegion = isInRegionSelector(NEWBUILDING_PURCHASE_GIFT_REGIONS);

interface INewbuildingPurchaseGiftPromoConfig {
  email: string;
  termsUrl: string;
}

interface IExperimentGateProps extends React.PropsWithChildren {
  config: INewbuildingPurchaseGiftPromoConfig;
}

const PromoModalHost: React.FC<IExperimentGateProps> = ({ config, children }) => {
  const [open, setOpen] = React.useState(false);
  /* Чанк модалки грузится только после первого открытия, дальше модалка остаётся смонтированной */
  const [isModalMounted, setIsModalMounted] = React.useState(false);
  const sourceRef = React.useRef<TNewbuildingPurchaseGiftPromoSource>('sidebar');
  const gallerySlideShownRef = React.useRef(false);

  const openModal = React.useCallback((source: TNewbuildingPurchaseGiftPromoSource) => {
    trackEntryPointClick(source);
    sourceRef.current = source;
    setIsModalMounted(true);
    setOpen(true);
  }, []);

  /* Слайд есть и в обычной, и в полноэкранной галерее: показ отправляется один раз на страницу */
  const trackGallerySlideShow = React.useCallback(() => {
    if (gallerySlideShownRef.current) {
      return;
    }

    gallerySlideShownRef.current = true;
    trackEntryPointShow('gallery');
  }, []);

  const contextValue = React.useMemo<INewbuildingPurchaseGiftPromoContext>(
    () => ({ isAvailable: true, openModal, trackGallerySlideShow }),
    [openModal, trackGallerySlideShow],
  );

  const handleShow = React.useCallback(() => trackModalShow(sourceRef.current), []);

  return (
    <NewbuildingPurchaseGiftPromoContext.Provider value={contextValue}>
      {children}
      {isModalMounted && (
        <NewbuildingPurchaseGiftModalLoadable
          open={open}
          onOpenChange={setOpen}
          config={config}
          onShow={handleShow}
          onClose={trackModalClose}
          onConfirmClick={trackModalConfirm}
          onCopyEmailClick={trackCopyEmail}
          onTermsClick={trackTermsClick}
          onStepView={trackStepView}
        />
      )}
    </NewbuildingPurchaseGiftPromoContext.Provider>
  );
};

const ExperimentGate: React.FC<IExperimentGateProps> = ({ config, children }) => {
  const group = NewbuildingPurchaseGiftExperiment.use();

  if (group !== 'B') {
    return children;
  }

  return <PromoModalHost config={config}>{children}</PromoModalHost>;
};

/**
 * Провайдер акции «30 000 ₽ за покупку квартиры» для новостроечной карточки:
 * считает доступность точек входа и держит общую модалку для язычка в сайдбаре и слайда галереи.
 * Эксперимент читается только когда тогл включён, конфиг заполнен и регион подходит,
 * чтобы exposure уходил лишь там, где точка входа могла показаться.
 */
export const NewbuildingPurchaseGiftPromoProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { config } = useApplicationContext();
  const isInRegion = useSelector(selectIsInPromoRegion);

  const isEnabled = Boolean(config.get<boolean>(NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY));
  const email = config.get<string>(NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY) || '';
  const termsUrl = config.get<string>(NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY) || '';

  if (!isEnabled || !email || !termsUrl || !isInRegion) {
    return children;
  }

  return <ExperimentGate config={{ email, termsUrl }}>{children}</ExperimentGate>;
};
